import type { ComponentType } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { TONE_SOFT, type Tone } from '../tone';
import { Icon } from '../Icon';

/** The shared status vocabulary (ui/tone.ts). */
export type IconBadgeTone = Tone;
export type IconBadgeSize = 'sm' | 'md' | 'lg';

export type IconBadgeProps = {
  icon: ComponentType<IconProps>;
  tone?: Tone;
  size?: IconBadgeSize;
  shape?: 'circle' | 'square';
  /** Only when the badge is the sole label of something; otherwise it is decorative. */
  label?: string;
  className?: string;
};

const SIZE: Record<IconBadgeSize, { box: string; icon: 'sm' | 'md' | 'lg' }> = {
  sm: { box: 'size-8', icon: 'sm' },
  md: { box: 'size-10', icon: 'md' },
  lg: { box: 'size-14', icon: 'lg' },
};

/** An icon on a soft accent disc or tile, marking a row, banner or empty state. */
export function IconBadge({ icon, tone = 'neutral', size = 'md', shape = 'circle', label, className }: IconBadgeProps) {
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cx(
        'inline-flex shrink-0 items-center justify-center',
        shape === 'circle' ? 'rounded-full' : 'rounded-jc-info',
        TONE_SOFT[tone],
        SIZE[size].box,
        className,
      )}
    >
      <Icon icon={icon} size={SIZE[size].icon} />
    </span>
  );
}
